// lectureValidation.js
import { z } from 'zod';
import { captureError } from '../../../../utils/sentryHelpers';

// ========== TEST EVENT CONFIGURATION ==========
const testEventSchema = {
  eventId: "tech-conference-2024",
  eventType: "conference",
  requiredFields: [
    "conditions",
    "title",
    "speakers", 
    "content",
    "image",
    "topics",
    "subjects"
  ],
  conditionalRules: [
    {
      condition: "eventType === 'workshop'",
      additionalRequired: ["maxParticipants", "duration", "prerequisites"]
    },
    {
      condition: "eventType === 'webinar'",
      additionalRequired: ["meetingLink", "recordingAllowed"],
      removeRequired: ["eventLocation"]
    },
    {
      condition: "isAdvancedLevel === true",
      additionalRequired: ["prerequisites", "certification"]
    }
  ],
  customValidations: {
    "content.text.minLength": 500,        // Override default 450
    "content.text.maxLength": 2000,       // Override default 2000
    // "maxParticipants.max": 25,           // Workshop specific
    "title.text.minLength": 10,          // Title minimum length
    "title.text.maxLength": 60,          // Title maximum length
    "speakers.min": 1                    // Conference needs min 2 speakers
  },
  fieldMessages: {
    // "maxParticipants": "Teilnehmerzahl",
    "duration": "Workshop-Dauer",
    "prerequisites": "Voraussetzungen",
    "meetingLink": "Meeting-Link",
    "recordingAllowed": "Aufzeichnung-Erlaubnis",
    "certification": "Zertifizierung"
  }
};

// ========== LOUNGES 26 EVENT CONFIGURATION ==========
const lounges26Schema = {
  eventId: "cb7a916e-ed71-483a-8177-22532ab0b237",
  eventType: "fair",
  
  // Event dates configuration
  lectureRegistrationStartDate: "2025-08-15",
  lectureRegistrationEndDate: "2025-11-30", 
  lectureEditEndDate: "2025-11-30",
  lectureDocumentsEndDate: "2026-01-31",
  lectureLectureEndDate: "2026-02-28",
  lectureRecordingCorrectionEndDate: "2026-04-05",
  
  // Information content for the information step (stored as HTML string)
  informationContent: [
    {
      language: "de",
      content: `
    <h4>Vortragseinreichung für die LOUNGES 2027</h4>
    
    <p>Bevor Du mit der Einreichung beginnst, lese die nachfolgenden Informationen bitte sorgfältig durch.<br>
    <strong>Nur wenn die genannten Termine und Standards eingehalten werden, kann der Vortrag angenommen werden.</strong></p>

    <h5>Wichtige Termine</h5>
    <ul>
        <li><strong>Beginn der Einreichung:</strong> 15. August 2026</li>
        <li><strong>Einreichungsschluss:</strong> 30. November 2026</li>
        <li><strong>Bestätigungsfrist eingereichter/unvollständiger Vorträge:</strong> 30. November 2026</li>
        <li><strong>Keine Einreichungen mehr ab:</strong> 1. Dezember 2026</li>
    </ul>
      <br />
    <h4>Vortragsarten</h4>
    <p>Folgende vier Vortragskategorien stehen zur Auswahl.</p>

    <h6><strong>VIP-Vortrag</strong></h6>
    <ul>
        <li>Vortrag gemeinsam mit einem Behördenvertreter</li>
        <li>Oder Vortrag über Projekte, die gemeinsam mit dem Kunden umgesetzt werden/wurden und gemeinsam mit mindestens 2 Personen auch vorgetragen werden
        <ul><li>Kunde bedeutet hier: Arzneimittelhersteller</li></ul></li>
        <li>Vortrag wird auf der Internetseite sowie zusätzlich separat über LinkedIn und anderen Medien einzeln beworben</li>
        <li>Studiobühne oder ECV-Stage</li>
    </ul>

    <h6><strong>Fachvortrag *</strong></h6>
    <ul>
        <li>Expertenwissen, Forschungsergebnisse, Guidelines, Innovationen, Kundenprojekte</li>
        <li>Vortrag wird auf der Internetseite sowie über das Programm über LinkedIn und anderen Medien einzeln beworben</li>
        <li>Vortragsraum 1,2,3,6,7,8</li>
    </ul>

    <h6><strong>Schulungsvortrag *</strong></h6>
    <ul>
        <li>Spezifisches Wissen, Fähigkeiten, Kompetenzen. Dient zur Schulung von Einsteigern bis Experten</li>
        <li>Vortrag wird auf der Internetseite sowie über das Programm über LinkedIn und anderen Medien einzeln beworben</li>
        <li>Vortragsraum 1,2,3,6,7,8</li>
    </ul>

    <h6><strong>Marketingvortrag</strong></h6>
    <ul>
        <li>Wert und Nutzen von Produkten, Dienstleistungen, Ideen. Präsentation der Marke. Für potenzielle Kunden, Geschäftspartner.</li>
        <li>Vortrag wird auf der Internetseite beworben</li>
        <li>Offener Vortragsbereich 4,5</li>
    </ul>

    <p><strong>Bitte beachten:</strong> VIP-, Fach- und Schulungsvorträge werden generell aufgezeichnet.</p>

    <span><em>*In Ausnahmefällen können diese auch auf Wunsch nicht aufgezeichnet werden. Sie kommen dann zunächst auf eine Warteliste und werden bei der Programmgestaltung erst ganz zum Schluss dem Zeitplan zugeordnet, falls noch freie Plätze vorhanden sind.</em></span>
    <ul>
    <li>Offener Vortragsbereich 9</li>
    </ul>
    <h5>Wichtiger Hinweis zu den Aufzeichnungen und Nutzungsrechten</h5>
    <p>VIP-, Fach- und Schulungsvorträge werden per Audio und Video aufgezeichnet. Die Aufzeichnungsrechte gehören der Inspire GmbH und werden Teilnehmern sowie Nicht-Teilnehmern auf der Online-Plattform angeboten. Der Zugang zu den Aufzeichnungen kann käuflich erworben werden.</p>

    <h5>Qualitätsstandards</h5>
    <ul>
        <li>Referenten sind Experten ihres Fachgebiets</li>
        <li>Vorträge müssen qualitativ hochwertig und didaktisch wertvoll sein</li>
        <li>VIP-, Fach- und Schulungsvorträge sind werbefrei</li>
        <li>Aussagekräftige Titel, informative Unterpunkte, mindestens 500 Zeichen Inhaltsbeschreibung und ein passendes Bild zum Thema des Vortrags erforderlich</li>
    </ul>
      `
    },
    {
      language: "en",
      content: `
        <h4>Lecture Submission for LOUNGES 2027</h4>
    
    <p>Before you begin with your submission, please read the following information carefully.<br>
    <strong>Only if the mentioned deadlines and standards are met can the presentation be accepted.</strong></p>

    <h5>Important Dates</h5>
    <ul>
        <li><strong>Start of submission:</strong> August 15, 2025</li>
        <li><strong>Submission deadline:</strong> November 30, 2025</li>
        <li><strong>Confirmation deadline for submitted/incomplete presentations:</strong> November 30, 2025</li>
        <li><strong>No more submissions from:</strong> December 1, 2025</li>
    </ul>
      <br />
    <h4>Lecture Types</h4>
    <p>The following four presentation categories are available for selection.</p>

    <h6><strong>VIP Lecture</strong></h6>
    <ul>
        <li>Lecture together with a government agency representative</li>
        <li>Or presentation about projects that are/were implemented together with the client and presented together with at least 2 people
        <ul><li>Client means here: pharmaceutical manufacturer</li></ul></li>
        <li>Lecture will be promoted on the website as well as separately via LinkedIn and other media</li>
        <li>Studio stage or ECV-Stage</li>
    </ul>

    <h6><strong>Expert Lecture *</strong></h6>
    <ul>
        <li>Expert knowledge, research results, guidelines, innovations, client projects</li>
        <li>Lecture will be promoted on the website as well as through the program via LinkedIn and other media individually</li>
        <li>Lecture room 1,2,3,6,7,8</li>
    </ul>

    <h6><strong>Training Lecture *</strong></h6>
    <ul>
        <li>Specific knowledge, skills, competencies. Serves to train beginners to experts</li>
        <li>Lecture will be promoted on the website as well as through the program via LinkedIn and other media individually</li>
        <li>Lecture room 1,2,3,6,7,8</li>
    </ul>

    <h6><strong>Marketing Lecture</strong></h6>
    <ul>
        <li>Value and benefits of products, services, ideas. Brand presentation. For potential customers, business partners.</li>
        <li>Lecture will be promoted on the website</li>
        <li>Open presentation area 4,5</li>
    </ul>

    <p><strong>Please note:</strong> VIP, expert and training presentations are generally recorded.</p>

    <span><em>*In exceptional cases, these can also not be recorded upon request. They will initially be placed on a waiting list and will be assigned to the schedule at the very end during program planning, if free slots are still available.</em></span>
      <ul>
        <li>Open presentation area 9</li>
      </ul>
    <h5>Important Notice Regarding Recordings and Usage Rights</h5>
    <p>VIP, expert and training presentations are recorded via audio and video. The recording rights belong to Inspire GmbH and are offered to participants as well as non-participants on the online platform. Access to the recordings can be purchased.</p>

    <h5>Quality Standards</h5>
    <ul>
        <li>Speakers are experts in their field</li>
        <li>Lectures must be of high quality and didactically valuable</li>
        <li>VIP, expert and training presentations are advertising-free</li>
        <li>Meaningful titles, informative bullet points, at least 500 characters content description and a suitable image related to the presentation topic required</li>
    </ul>
      `
    }
  ],
  
  // Available lecture types for this event
  availableLectureTypes: [
    "vip",
    "technical", 
    "educational",
    "marketing",
    // "promotion_technical"
  ],
  
  // General conditions that apply to all users regardless of lecture type
   generalConditions: [
    {
      id: "submission_deadlines",
      title: [
        { language: "de", text: "Einreichungsfristen" },
        { language: "en", text: "Submission Deadlines" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich habe die Einreichungsfristen gelesen und akzeptiere diese." },
        { language: "en", text: "I have read and accept the submission deadlines." }
      ],
      content: [
        { language: "de", text: "Vorträge können ab dem 15. August 2026 eingereicht und müssen vollständig bis 30. November 2026 bestätigt werden. Nach dem 1. Dezember 2026 können keine Vorträge mehr eingereicht werden. Unvollständige Vorträge nach dem 30. November 2026 werden nicht ins Programm aufgenommen." },
        { language: "en", text: "Lectures can be submitted from August 15, 2025 and must be completely confirmed by November 30, 2025. No lectures can be submitted after December 1, 2025. Incomplete lectures after November 30, 2025 will not be included in the program." }
      ],
      required: true
    },
    {
      id: "advertising_free_content",
      title: [
        { language: "de", text: "Werbefreiheit der Vorträge" },
        { language: "en", text: "Advertising-Free Content" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich habe verstanden, dass der Vortrag werbefrei sein muss und akzeptiere dies." },
        { language: "en", text: "I understand that the lecture must be advertising-free and accept this." }
      ],
      content: [
        { language: "de", text: "VIP-, Fach- und Schulungsvorträge sind werbefrei (keine Produktnamen, Datenblätter oder Firmeninformationen)." },
        { language: "en", text: "VIP, technical and educational lectures must be advertising-free (no product names, data sheets or company information)." }
      ],
      required: true
    },
    {
      id: "recording_policy",
      title: [
        { language: "de", text: "Aufzeichnungsrichtlinien" },
        { language: "en", text: "Recording Policy" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich bin über die Aufzeichnungsrichtlinien informiert und akzeptiere diese." },
        { language: "en", text: "I am aware of and accept the recording policy." }
      ],
      content: [
        { language: "de", text: "VIP-, Fach- und Schulungsvorträge werden per Audio und Video aufgezeichnet. Die Aufzeichnungsrechte gehören der Inspire GmbH. Aufzeichnungen werden Teilnehmern und Nicht-Teilnehmern auf der Online-Plattform angeboten. Eigene Vorträge stehen Admins und Referenten kostenfrei zur Verfügung (Download nicht möglich). Aufzeichnungen können bis 5. April 2027 korrigiert werden, danach werden sie automatisch freigegeben." },
        { language: "en", text: "VIP, technical and educational lectures will be recorded in audio and video. Recording rights belong to Inspire GmbH. Recordings will be offered to participants and non-participants on the online platform. Your own lectures are available to admins and speakers free of charge (download not possible). Recordings can be corrected until April 5, 2026, after which they will be automatically released." }
      ],
      required: true
    },
    {
      id: "speaker_expertise",
      title: [
        { language: "de", text: "Qualifikation der Referenten" },
        { language: "en", text: "Speaker Expertise" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich bestätige, dass die Referenten qualifiziert sind." },
        { language: "en", text: "I confirm that the speakers are qualified." }
      ],
      content: [
        { language: "de", text: "Referenten sind Experten ihres Themas und bieten qualitativ hochwertige, didaktisch wertvolle Vorträge und haben kein Problem damit vor der Kamera zu präsentieren, sofern der Vortrag aufgezeichnet wird." },
        { language: "en", text: "Speakers are experts in their field and provide high-quality, didactically valuable lectures and have no problem presenting in front of the camera when the lecture is being recorded." }
      ],
      required: true
    },
    {
      id: "program_scheduling",
      title: [
        { language: "de", text: "Programmplanung" },
        { language: "en", text: "Program Scheduling" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich bin mit der Programmgestaltung / Zeiteinteilung durch den Veranstalter einverstanden." },
        { language: "en", text: "I agree with the program design / scheduling by the organizer." }
      ],
      content: [
        { language: "de", text: "Die Programmzuordnung erfolgt durch den Veranstalter und wird dem Admin und Referent bis zum 31. Januar 2027 mitgeteilt." },
        { language: "en", text: "Program scheduling is done by the organizer and will be communicated to the admin and speaker by January 31, 2026." }
      ],
      required: true
    },
    {
      id: "file_upload_deadlines",
      title: [
        { language: "de", text: "Fristen für Datei-Uploads" },
        { language: "en", text: "File Upload Deadlines" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich akzeptiere die Upload-Fristen für Vortragsunterlagen." },
        { language: "en", text: "I accept the upload deadlines for lecture materials." }
      ],
      content: [
        { language: "de", text: `Die Vortragsunterlagen werden den Teilnehmern der Veranstaltung zur Verfügung gestellt. Diese müssen bis zum folgenden Zeitpunkt in das System hochgeladen werden: 31. Januar 2027. Wenn bis zu diesem Datum keine Unterlagen hochgeladen wurden, wird der Vortrag aus dem Programm genommen.
          Präsentationsfolien (ppt oder pptx) für VIP-Vorträge, Fachvorträge und Schulungsvorträge müssen bis 28. Februar 2027 im System hochgeladen werden. Präsentationsfolien werden zur Aufzeichnung der Vorträge genutzt und nicht weitergegeben bzw. veröffentlicht.` },
        { language: "en", text: `Lecture materials / handouts (PDF) will be made available to event participants. Lecture materials must be uploaded to the system by January 31, 2026. If no lecture materials have been uploaded by this deadline, the lecture will be removed from the program.
          Lecture slides (PPT or PPTX) for VIP lectures, technical lectures and educational lectures must be uploaded to the system by February 28, 2026. Lecture slides are used for recording lectures and will not be shared or published.` }
      ],
      required: true
    }
  ],
  
  // Event-specific conditions with multilingual content
  eventConditions: [
    {
      id: "data_protection",
      content: [
        { language: "de", text: "Ich stimme der Verarbeitung meiner Daten gemäß der Datenschutzerklärung zu." },
        { language: "en", text: "I agree to the processing of my data according to the privacy policy." }
      ],
      applicableToTypes: ["vip", "technical", "educational", "marketing", "promotion_technical"],
      required: true
    },
    {
      id: "vip_special_terms",
      content: [
        { language: "de", text: "Ich bestätige, dass dieser VIP-Vortrag exklusiven Content bietet und mindestens 2 Referenten erfordert." },
        { language: "en", text: "I confirm that this VIP lecture offers exclusive content and requires at least 2 speakers." }
      ],
      applicableToTypes: ["vip"],
      required: true
    },
    {
      id: "promotion_disclaimer",
      content: [
        { language: "de", text: "Ich verstehe, dass Promotion-Vorträge marketingorientierten Inhalt enthalten können." },
        { language: "en", text: "I understand that promotion lectures may contain marketing-oriented content." }
      ],
      applicableToTypes: ["marketing", "promotion_technical"],
      required: false
    },
    {
      id: "technical_prerequisites",
      content: [
        { language: "de", text: "Ich bestätige, dass die technischen Voraussetzungen für diesen Vortrag erfüllt sind." },
        { language: "en", text: "I confirm that the technical prerequisites for this lecture are met." }
      ],
      applicableToTypes: ["technical", "promotion_technical"],
      required: false
    }
  ],

  requiredFields: [
    "companyLecturesId",
    "type",
    "generalConditions",
    // "conditions",
    "title",
    "speakers", 
    "content",
    "image",
    "topics",
    // "handout",
    "subjects"
  ],
  
  conditionalRules: [
    {
      condition: "type === 'vip'",
      additionalRequired: [],
      customValidations: {
        "speakers.min": 2
      }
    },
    {
      condition: "type === 'technical'",
      additionalRequired: [],
      customValidations: {
        "speakers.min": 1
      }
    },
    {
      condition: "type === 'educational'",
      additionalRequired: [],
      customValidations: {
        "speakers.min": 1
      }
    },
    {
      condition: "type === 'marketing'",
      additionalRequired: [],
      customValidations: {
        "speakers.min": 1
      }
    },
    {
      condition: "type === 'promotion_technical'",
      additionalRequired: [],
      customValidations: {
        "speakers.min": 1
      }
    }
  ],
  
  customValidations: {
    "content.text.minLength": 500,
    "content.text.maxLength": 5000,
    "title.text.minLength": 10,
    "title.text.maxLength": 60,
    "speakers.min": 1,  // Default minimum, can be overridden by conditional rules
    "topics.min": 1,    // Minimum topics required
    "topics.max": 3     // Maximum topics allowed
  },
  
  fieldMessages: {
    "type": "Lecture type is required"
  }
};

// ========== BASE SCHEMAS ==========

// Sprachspezifische Text-Struktur
const languageTextSchema = z.object({
  language: z.string().min(1, "Language is required"),
  text: z.string().min(1, "Text is required")
});

// Content Item mit konfigurierbarer Mindest- und Maximallänge
const createContentItemSchema = (minLength = 450, maxLength = 2000) => z.object({
  language: z.string({ message: "Language is required" }),
  text: z.string()
    .min(minLength, `Text must be at least ${minLength} characters long`)
    .max(maxLength, `Text may be at most ${maxLength} characters long`)
});

// Subject Schema - matches actual usage in SubjectsStep
const subjectSchema = z.object({
  de: z.string(),
  en: z.string()
});

// Legacy Subject Schema (for potential future use)
const legacySubjectSchema = z.object({
  name: z.array(languageTextSchema).min(2, "Name in at least 2 languages required").optional(),
  description: z.array(languageTextSchema).optional(),
  order: z.number().optional()
});

// Outline Point Schema for lecture structure
const outlinePointSchema = z.object({
  id: z.string().min(1, "Outline point ID is required"),
  title: z.string().min(1, "Outline point title is required")
});

// Condition Schema
const conditionSchema = z.object({
  id: z.string(),
  accepted: z.boolean().refine(val => val === true, {
    message: "Condition must be accepted"
  })
});

// Speaker Schema (erweitert für Events - unterstützt Employee und User Speaker)
const speakerSchema = z.object({
  id: z.string(),
  speakerType: z.enum(['employee', 'user']).optional(),
  employeeId: z.string().optional(),
  userId: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  middleName: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  department: z.string().optional().nullable(),
  image: z.object({
    fileName: z.string(),
    alt: z.string().optional().nullable()
  }).optional().nullable(),
  type: z.enum(['main_speaker', 'co_speaker']),
  // Legacy fields for backward compatibility
  name: z.string().optional(),
  company: z.string().optional()
}).refine((data) => {
  // Ensure speaker has either employeeId or userId based on speakerType
  if (data.speakerType === 'employee') {
    return !!data.employeeId;
  } else if (data.speakerType === 'user') {
    return !!data.userId;
  }
  // If speakerType is not set, accept if either ID exists (backward compatibility)
  return !!(data.employeeId || data.userId);
}, {
  message: "Speaker must have either employeeId (for employees) or userId (for users)"
});

// ========== COMPLETE BASE SCHEMA ==========
const createBaseLectureSchema = () => z.object({
  industry: z.string(),
  eventLecturesId: z.string(),
  type: z.string(),
  companyLecturesId: z.string().optional(),


  // Standard Felder (immer optional in Base)
  title: z.array(languageTextSchema).min(1).optional(),
  speakers: z.array(speakerSchema).min(1).optional(),
  subjects: z.array(subjectSchema).min(3).max(5).optional(),
  content: z.array(createContentItemSchema()).min(1).optional(),
  generalConditions: z.array(conditionSchema).min(1).optional(),
  conditions: z.array(conditionSchema).min(1).optional(),
  outline: z.array(outlinePointSchema).min(1).optional(),
  image: z.object({
    fileName: z.string(),
    url: z.string().url().optional(),
    alt: z.array(languageTextSchema).optional()
  }).optional(),
  handout: z.object({
    de: z.object({
      fileName: z.string(),
      file: z.any().optional()
    }).optional(),
    en: z.object({
      fileName: z.string(),
      file: z.any().optional()
    }).optional()
  }).optional(),
  topics: z.array(z.string()).min(1).optional(),
  
  // Event-spezifische Felder
  eventLocation: z.string().optional(),
  targetAudience: z.array(languageTextSchema).optional(),
  maxParticipants: z.number().min(1).optional(),
  duration: z.number().min(1).optional(), // in Minuten
  prerequisites: z.array(languageTextSchema).optional(),
  meetingLink: z.string().url().optional(),
  recordingAllowed: z.boolean().optional(),
  certification: z.object({
    available: z.boolean(),
    provider: z.string().optional(),
    validityPeriod: z.number().optional() // in Monaten
  }).optional(),
  
  // Zusätzliche Metadaten
  isAdvancedLevel: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

// ========== DYNAMIC SCHEMA BUILDER ==========
const createEventSpecificLectureSchema = (eventConfig, lectureData = {}) => {
  const { requiredFields = [], conditionalRules, customValidations, fieldMessages, availableLectureTypes, eventConditions } = eventConfig;

  let finalRequiredFields = [...(requiredFields || [])];
  let finalCustomValidations = { ...customValidations };
  
  // Create dummy data for condition evaluation
  const dummyEventData = {
    eventType: eventConfig.eventType,
    type: lectureData.type || null,
    isAdvancedLevel: false
  };
  
  // Apply conditional rules based on lecture type and other conditions
  conditionalRules?.forEach(rule => {
    const conditionMet = evaluateCondition(rule.condition, dummyEventData);
    
    if (conditionMet) {
      finalRequiredFields.push(...(rule.additionalRequired || []));
      finalRequiredFields = finalRequiredFields.filter(
        field => !rule.removeRequired?.includes(field)
      );
      
      // Merge custom validations from conditional rules
      if (rule.customValidations) {
        finalCustomValidations = { ...finalCustomValidations, ...rule.customValidations };
      }
    }
  });
  
  // Remove duplicates
  finalRequiredFields = [...new Set(finalRequiredFields)];
  
  // Schema Building
  const schemaFields = {};
  const baseSchema = createBaseLectureSchema();
  
  finalRequiredFields.forEach(field => {
    const customMessage = fieldMessages?.[field];
    
    switch(field) {
      case 'type':
        if (availableLectureTypes && availableLectureTypes.length > 0) {
          schemaFields.type = z.enum(availableLectureTypes, {
            message: customMessage || "Valid lecture type required"
          });
        } else {
          schemaFields.type = z.string().min(1, customMessage || "Lecture type required");
        }
        break;
        
      case 'title':
        // Default title validation rules (can be overridden by event-specific customValidations)
        const minTitleLength = finalCustomValidations['title.text.minLength'] || 10; // Default: 10 characters minimum
        const maxTitleLength = finalCustomValidations['title.text.maxLength'] || 60; // Default: 60 characters maximum
        
        const titleWithLengthConstraints = z.object({
          language: z.string().min(1),
          text: z.string()
            .min(minTitleLength, `Title must be at least ${minTitleLength} characters long`)
            .max(maxTitleLength, `Title may be at most ${maxTitleLength} characters long`)
        });
        
        const titleSchema = z.union([
          z.array(titleWithLengthConstraints).min(2),
          z.any()
        ]).refine((data) => {
          // Handle case where data is not an array (e.g., empty string or undefined)
          if (!Array.isArray(data)) {
            return false;
          }
          
          // Check if we have both required languages
          const requiredLanguages = ['de', 'en'];
          const providedLanguages = data.map(item => item.language).filter(Boolean);
          const missingLanguages = requiredLanguages.filter(lang => !providedLanguages.includes(lang));
          
          // Check if provided languages have valid text
          const emptyLanguages = data
            .filter(item => item.language && requiredLanguages.includes(item.language))
            .filter(item => !item.text || item.text.trim().length === 0)
            .map(item => item.language);
          
          const invalidLengthLanguages = data
            .filter(item => item.language && requiredLanguages.includes(item.language))
            .filter(item => item.text && item.text.trim().length > 0)
            .filter(item => item.text.length < minTitleLength || item.text.length > maxTitleLength)
            .map(item => item.language);
          
          return missingLanguages.length === 0 && emptyLanguages.length === 0 && invalidLengthLanguages.length === 0;
        }, (data) => {
          // Custom error message based on what's missing
          if (!Array.isArray(data)) {
            return { message: "Title in German and English required" };
          }
          
          const requiredLanguages = ['de', 'en'];
          const languageNames = { 'de': 'German', 'en': 'English' };
          const providedLanguages = data.map(item => item.language).filter(Boolean);
          const missingLanguages = requiredLanguages.filter(lang => !providedLanguages.includes(lang));
          
          if (missingLanguages.length > 0) {
            const missingNames = missingLanguages.map(lang => languageNames[lang]).join(' and ');
            return { message: `Title in ${missingNames} required` };
          }
          
          // Check for empty titles
          const emptyLanguages = data
            .filter(item => item.language && requiredLanguages.includes(item.language))
            .filter(item => !item.text || item.text.trim().length === 0)
            .map(item => item.language);
          
          if (emptyLanguages.length > 0) {
            const emptyNames = emptyLanguages.map(lang => languageNames[lang]).join(' and ');
            return { message: `Title in ${emptyNames} may not be empty` };
          }
          
          // Check for invalid lengths
          const invalidLengthLanguages = data
            .filter(item => item.language && requiredLanguages.includes(item.language))
            .filter(item => item.text && item.text.trim().length > 0)
            .filter(item => item.text.length < minTitleLength || item.text.length > maxTitleLength)
            .map(item => item.language);
          
          if (invalidLengthLanguages.length > 0) {
            const invalidNames = invalidLengthLanguages.map(lang => languageNames[lang]).join(' and ');
            return { message: `Title in ${invalidNames} must be between ${minTitleLength} and ${maxTitleLength} characters long` };
          }
          
          return { message: "Title in German and English required" };
        });
        
        schemaFields.title = titleSchema;
        break;
        
      case 'speakers':
        const minSpeakers = finalCustomValidations['speakers.min'] || 1;
        schemaFields.speakers = z.array(speakerSchema)
          .min(minSpeakers, customMessage || `At least ${minSpeakers} speaker(s) required`)
          .refine(speakers => {
            // Ensure at least one main speaker is present
            const mainSpeakers = speakers.filter(speaker => speaker.type === 'main_speaker');
            return mainSpeakers.length >= 1;
          }, {
            message: "At least one main speaker must be selected"
          })
          .refine(speakers => {
            // Ensure all main speakers have valid IDs (employeeId OR userId)
            const mainSpeakers = speakers.filter(speaker => speaker.type === 'main_speaker');
            return mainSpeakers.every(speaker => {
              if (speaker.speakerType === 'employee') {
                return speaker.employeeId && speaker.employeeId.length > 0;
              } else if (speaker.speakerType === 'user') {
                return speaker.userId && speaker.userId.length > 0;
              }
              // Backward compatibility: accept if either ID exists
              return (speaker.employeeId && speaker.employeeId.length > 0) ||
                     (speaker.userId && speaker.userId.length > 0);
            });
          }, {
            message: "All main speakers must have a valid ID"
          });
        break;
        
      case 'content':
        const minLength = finalCustomValidations['content.text.minLength'] || 450;
        const maxLength = finalCustomValidations['content.text.maxLength'] || 2000;
        
        const contentSchema = z.union([
          z.array(createContentItemSchema(minLength, maxLength)).min(2),
          z.any()
        ]).refine((data) => {
          // Handle case where data is not an array (e.g., empty string or undefined)
          if (!Array.isArray(data)) {
            return false;
          }
          
          // Check if we have both required languages
          const requiredLanguages = ['de', 'en'];
          const providedLanguages = data.map(item => item.language).filter(Boolean);
          const missingLanguages = requiredLanguages.filter(lang => !providedLanguages.includes(lang));
          
          // Check if provided languages have valid text
          const emptyLanguages = data
            .filter(item => item.language && requiredLanguages.includes(item.language))
            .filter(item => !item.text || item.text.trim().length === 0)
            .map(item => item.language);
          
          const invalidLengthLanguages = data
            .filter(item => item.language && requiredLanguages.includes(item.language))
            .filter(item => item.text && item.text.trim().length > 0)
            .filter(item => item.text.length < minLength || item.text.length > maxLength)
            .map(item => item.language);
          
          return missingLanguages.length === 0 && emptyLanguages.length === 0 && invalidLengthLanguages.length === 0;
        }, (data) => {
          // Custom error message based on what's missing
          if (!Array.isArray(data)) {
            return { message: "Description in German and English required" };
          }
          
          const requiredLanguages = ['de', 'en'];
          const languageNames = { 'de': 'German', 'en': 'English' };
          const providedLanguages = data.map(item => item.language).filter(Boolean);
          const missingLanguages = requiredLanguages.filter(lang => !providedLanguages.includes(lang));
          
          if (missingLanguages.length > 0) {
            const missingNames = missingLanguages.map(lang => languageNames[lang]).join(' and ');
            return { message: `Description in ${missingNames} required` };
          }
          
          // Check for empty descriptions
          const emptyLanguages = data
            .filter(item => item.language && requiredLanguages.includes(item.language))
            .filter(item => !item.text || item.text.trim().length === 0)
            .map(item => item.language);
          
          if (emptyLanguages.length > 0) {
            const emptyNames = emptyLanguages.map(lang => languageNames[lang]).join(' and ');
            return { message: `Description in ${emptyNames} may not be empty` };
          }
          
          // Check for invalid lengths
          const invalidLengthLanguages = data
            .filter(item => item.language && requiredLanguages.includes(item.language))
            .filter(item => item.text && item.text.trim().length > 0)
            .filter(item => item.text.length < minLength || item.text.length > maxLength)
            .map(item => item.language);
          
          if (invalidLengthLanguages.length > 0) {
            const invalidNames = invalidLengthLanguages.map(lang => languageNames[lang]).join(' and ');
            return { message: `Description in ${invalidNames} must be between ${minLength} and ${maxLength} characters long` };
          }
          
          return { message: "Description in German and English required" };
        });
        
        schemaFields.content = contentSchema;
        break;
        
      case 'subjects':
        schemaFields.subjects = z.union([
          z.array(subjectSchema),
          z.null(),
          z.undefined()
        ])
          .transform((data) => {
            // Transform null/undefined to empty array for consistent validation
            if (data === null || data === undefined) {
              return [];
            }
            return data;
          })
          .pipe(
            z.array(subjectSchema)
              .min(3, customMessage || "At least 3 subpoints required")
              .max(5, "Maximum 5 subpoints allowed")
              .refine(subjects => {
                // At least 3 subjects must be completely filled (both de and en)
                const completelyFilledSubjects = subjects.filter(subject => 
                  subject.de && subject.de.trim().length > 0 &&
                  subject.en && subject.en.trim().length > 0
                );
                return completelyFilledSubjects.length >= 3;
              }, {
                message: "At least 3 subpoints must be completely filled in (both languages)"
              })
              .refine(subjects => {
                // If any subject is partially filled, it must be completely filled
                return subjects.every(subject => {
                  const hasDeContent = subject.de && subject.de.trim().length > 0;
                  const hasEnContent = subject.en && subject.en.trim().length > 0;
                  
                  // If neither has content, that's fine (empty subject)
                  if (!hasDeContent && !hasEnContent) return true;
                  
                  // If either has content, both must have content
                  return hasDeContent && hasEnContent;
                });
              }, {
                message: "When a subpoint is filled in, both languages (German and English) must be filled in"
              })
          );
        break;
        
      case 'outline':
        const minOutlinePoints = finalCustomValidations['outline.min'] || 3;
        const maxOutlinePoints = finalCustomValidations['outline.max'] || 8;
        schemaFields.outline = z.array(outlinePointSchema)
          .min(minOutlinePoints, customMessage || `At least ${minOutlinePoints} outline points required`)
          .max(maxOutlinePoints, customMessage || `Maximum ${maxOutlinePoints} outline points allowed`)
          .refine(points => {
            // Check that all titles are filled
            return points.every(point => point.title && point.title.trim().length > 0);
          }, {
            message: "All outline point titles must be filled in"
          });
        break;
        
      case 'generalConditions':
        // Create general conditions schema
        let generalConditionsSchema;
        if (eventConfig.generalConditions) {
          // Create enum for valid general condition IDs
          const validGeneralConditionIds = eventConfig.generalConditions.map(c => c.id);
          const requiredGeneralConditions = eventConfig.generalConditions.filter(c => c.required).map(c => c.id);
          
          generalConditionsSchema = z.array(conditionSchema)
            .refine(conditions => {
              // Check if all required general conditions are present and accepted
              const acceptedConditionIds = conditions
                .filter(c => c.accepted)
                .map(c => c.id);
              
              return requiredGeneralConditions.every(reqId => acceptedConditionIds.includes(reqId));
            }, {
              message: `All required general conditions must be accepted`
            })
            .refine(conditions => {
              // Check if all condition IDs are valid general conditions
              return conditions.every(c => validGeneralConditionIds.includes(c.id));
            }, {
              message: `Invalid general condition`
            });
        } else {
          // Fallback to basic conditions schema
          generalConditionsSchema = z.array(conditionSchema)
            .min(1, customMessage || "General conditions");
        }
        
        schemaFields.generalConditions = generalConditionsSchema;
        break;
        
      case 'conditions':
        // Create dynamic condition schema based on lecture type
        let conditionsSchema;
        if (eventConditions && lectureData.type) {
          // Filter conditions applicable to the current lecture type
          const applicableConditions = eventConditions.filter(condition => 
            condition.applicableToTypes.includes(lectureData.type)
          );
          
          // Create enum for valid condition IDs
          const validConditionIds = applicableConditions.map(c => c.id);
          const requiredConditions = applicableConditions.filter(c => c.required).map(c => c.id);
          
          conditionsSchema = z.array(conditionSchema)
            .refine(conditions => {
              // Check if all required conditions are present and accepted
              const acceptedConditionIds = conditions
                .filter(c => c.accepted)
                .map(c => c.id);
              
              return requiredConditions.every(reqId => acceptedConditionIds.includes(reqId));
            }, {
              message: `All required conditions must be accepted`
            })
            .refine(conditions => {
              // Check if all condition IDs are valid for this lecture type
              return conditions.every(c => validConditionIds.includes(c.id));
            }, {
              message: `Invalid condition for this lecture type`
            });
        } else {
          // Fallback to basic conditions schema
          conditionsSchema = z.array(conditionSchema)
            .min(1, customMessage || "Conditions");
        }
        
        schemaFields.conditions = conditionsSchema;
        break;
        
      case 'image':
        schemaFields.image = z.union([
          z.object({
            fileName: z.string({ message: customMessage || "Image required" }),
            url: z.string().url().optional(),
            alt: z.union([
              z.string().optional(),
              z.array(languageTextSchema).optional()
            ]).optional()
          }),
          z.null(),
          z.undefined()
        ]).refine(
          (data) => data !== null && data !== undefined && data.fileName,
          {
            message: customMessage || "Please upload an image for the lecture"
          }
        );
        break;
        
      case 'topics':
        const minTopics = finalCustomValidations['topics.min'] || 1;
        const maxTopics = finalCustomValidations['topics.max'] || 3;
        schemaFields.topics = z.array(z.string())
          .min(minTopics, customMessage || `At least ${minTopics} topic area${minTopics > 1 ? 's' : ''} required`)
          .max(maxTopics, customMessage || `Maximum ${maxTopics} topic areas allowed`);
        break;
        
      case 'eventLocation':
        schemaFields.eventLocation = z.string()
          .min(1, customMessage || "Event location required");
        break;
        
      case 'targetAudience':
        schemaFields.targetAudience = z.array(languageTextSchema)
          .min(1, customMessage || "Target audience required");
        break;
        
      case 'maxParticipants':
        const maxLimit = finalCustomValidations['maxParticipants.max'] || 100;
        schemaFields.maxParticipants = z.number()
          .min(1, "At least 1 participant")
          .max(maxLimit, customMessage || `Maximum ${maxLimit} participants`);
        break;
        
      case 'duration':
        const minDuration = finalCustomValidations['duration.min'] || 30;
        const maxDuration = finalCustomValidations['duration.max'] || 600;
        schemaFields.duration = z.number()
          .min(minDuration, `At least ${minDuration} minutes`)
          .max(maxDuration, `Maximum ${maxDuration} minutes`);
        break;
        
      case 'prerequisites':
        schemaFields.prerequisites = z.array(languageTextSchema)
          .min(1, customMessage || "Prerequisites required");
        break;
        
      case 'meetingLink':
        schemaFields.meetingLink = z.string()
          .url(customMessage || "Valid meeting link required");
        break;
        
      case 'recordingAllowed':
        schemaFields.recordingAllowed = z.boolean({
          message: customMessage || "Recording permission required"
        });
        break;
        
      case 'certification':
        schemaFields.certification = z.object({
          available: z.boolean(),
          provider: z.string().optional(),
          validityPeriod: z.number().min(1).optional()
        });
        break;
        
      case 'handout':
        schemaFields.handout = z.object({
          de: z.object({
            fileName: z.string(),
            file: z.any().optional()
          }).optional(),
          en: z.object({
            fileName: z.string(),
            file: z.any().optional()
          }).optional()
        }).refine(
          (data) => {
            // At least one handout (DE or EN) must be uploaded
            return data && (data.de || data.en);
          },
          {
            message: customMessage || "At least one handout (German or English) must be uploaded"
          }
        );
        break;
        
      default:
        // Fallback für nicht definierte Felder
        schemaFields[field] = baseSchema.shape[field] || z.any();
        break;
    }
  });
  
  return z.object(schemaFields);
};

// ========== HELPER FUNCTIONS ==========
const evaluateCondition = (condition, data) => {
  try {
    // Simple equality condition parser (handles patterns like "key === value")
    const equalityRegex = /^\s*(\w+)\s*===\s*(['"]?)([^'"]*)\2\s*$/;
    const match = condition.match(equalityRegex);

    if (match) {
      const [, key, quote, value] = match;
      const dataValue = data[key];
      const compareValue = quote ? value :
                          value === 'true' ? true :
                          value === 'false' ? false :
                          Number(value);
      return dataValue === compareValue;
    }

    // For complex conditions that we can't safely parse
    console.warn('Unsupported condition format:', condition);
    return false;
  } catch (error) {
    console.warn('Condition evaluation failed:', condition, error);
    captureError(error, {
      feature: 'lecture-validation',
      operation: 'evaluate-condition',
      consoleMessage: 'Condition evaluation failed:',
      context: { condition }
    });
    return false;
  }
};

// ========== VALIDATION FUNCTIONS ==========
export const validateLectureForEvent = (lectureData, eventConfig = testEventSchema) => {
  try {
    // If eventConfig is already a Zod schema, use it directly
    if (eventConfig && typeof eventConfig.safeParse === 'function') {
      const result = eventConfig.safeParse(lectureData);

      if (!result.success) {
        return {
          success: false,
          errors: result.error.errors.map(err => ({
            field: err.path.join('.'),
            message: err.message,
            code: err.code
          }))
        };
      }

      return {
        success: true,
        data: result.data,
        eventId: eventConfig.eventId
      };
    }

    // Otherwise, build schema from config
    const schema = createEventSpecificLectureSchema(eventConfig, lectureData);
    const result = schema.safeParse(lectureData);
    
    if (!result.success) {
      return {
        success: false,
        errors: result.error.errors.map(err => ({
          field: err.path.join('.'),
          message: err.message,
          code: err.code
        }))
      };
    }
    
    return { 
      success: true, 
      data: result.data,
      eventId: eventConfig.eventId 
    };
    
  } catch (error) {
    console.error('Schema creation error:', error);
    captureError(error, {
      feature: 'lecture-validation',
      operation: 'validate-lecture-for-event',
      consoleMessage: 'Schema creation error:',
      context: { eventConfig }
    });
    return {
      success: false,
      errors: [{
        field: 'schema',
        message: 'Validation configuration error',
        code: 'SCHEMA_ERROR'
      }]
    };
  }
};

// ========== HELPER FUNCTIONS FOR LECTURE TYPES AND CONDITIONS ==========

// Get available conditions for a specific lecture type
const getConditionsForLectureType = (eventConfig, lectureType) => {
  if (!eventConfig.eventConditions || !lectureType) {
    return [];
  }
  
  return eventConfig.eventConditions.filter(condition => 
    condition.applicableToTypes.includes(lectureType)
  );
};

// Get required conditions for a specific lecture type
const getRequiredConditionsForLectureType = (eventConfig, lectureType) => {
  const applicableConditions = getConditionsForLectureType(eventConfig, lectureType);
  return applicableConditions.filter(condition => condition.required);
};

// Validate lecture type against event configuration
const isValidLectureType = (eventConfig, lectureType) => {
  if (!eventConfig.availableLectureTypes || !lectureType) {
    return false;
  }
  
  return eventConfig.availableLectureTypes.includes(lectureType);
};

// Get speaker requirements for a lecture type
const getSpeakerRequirementsForType = (eventConfig, lectureType) => {
  if (!eventConfig.conditionalRules || !lectureType) {
    return { min: eventConfig.customValidations?.['speakers.min'] || 1 };
  }
  
  const typeRule = eventConfig.conditionalRules.find(rule => 
    rule.condition === `type === '${lectureType}'`
  );
  
  if (typeRule && typeRule.customValidations?.['speakers.min']) {
    return { min: typeRule.customValidations['speakers.min'] };
  }
  
  return { min: eventConfig.customValidations?.['speakers.min'] || 1 };
};

// Get general conditions for the event
const getGeneralConditions = (eventConfig) => {
  if (!eventConfig.generalConditions) {
    return [];
  }

  return eventConfig.generalConditions;
};

// Get required general conditions for the event
const getRequiredGeneralConditions = (eventConfig) => {
  if (!eventConfig.generalConditions) {
    return [];
  }

  return eventConfig.generalConditions.filter(condition => condition.required);
};

// Get validation rules for a specific field from event configuration
const getFieldValidationRules = (eventConfig, fieldName, lectureData = {}) => {
  const { customValidations, conditionalRules } = eventConfig;
  
  let finalCustomValidations = { ...customValidations };
  
  // Create dummy data for condition evaluation
  const dummyEventData = {
    eventType: eventConfig.eventType,
    type: lectureData.type || null,
    isAdvancedLevel: false
  };
  
  // Apply conditional rules to get final validations
  conditionalRules?.forEach(rule => {
    const conditionMet = evaluateCondition(rule.condition, dummyEventData);
    
    if (conditionMet && rule.customValidations) {
      finalCustomValidations = { ...finalCustomValidations, ...rule.customValidations };
    }
  });
  
  // Extract rules for the specific field
  const fieldRules = {};
  Object.keys(finalCustomValidations).forEach(key => {
    if (key.startsWith(`${fieldName}.`)) {
      const ruleName = key.replace(`${fieldName}.`, '');
      fieldRules[ruleName] = finalCustomValidations[key];
    }
  });
  
  return fieldRules;
};

// Get lecture type display name
const getLectureTypeName = (lectureType, language = 'en') => {
  const typeNames = {
    'vip': {
      'de': 'VIP-Vortrag',
      'en': 'VIP Lecture'
    },
    'technical': {
      'de': 'Fachvortrag',
      'en': 'Technical Lecture'
    },
    'educational': {
      'de': 'Schulungsvortrag',
      'en': 'Training Lecture'
    },
    'marketing': {
      'de': 'Marketingvortrag',
      'en': 'Marketing Lecture'
    },
    'promotion_technical': {
      'de': 'Technischer Promotion-Vortrag',
      'en': 'Technical Promotion'
    }
  };
  
  return typeNames[lectureType]?.[language] || lectureType;
};

// Get topics validation limits from event configuration
const getTopicsLimits = (eventConfig, lectureData = {}) => {
  const rules = getFieldValidationRules(eventConfig, 'topics', lectureData);
  return {
    min: rules.min || 1,
    max: rules.max || 3
  };
};

// Check if a step should be considered completed based on data and validation
const isStepDataComplete = (stepId, eventConfig, lectureData) => {
  // Custom completion logic for each step based on the validation schema
  switch (stepId) {
    case 'information':
      return lectureData.hasReadInformation === true;
      
    case 'general_conditions':
      // Check if all required general conditions are accepted
      if (!eventConfig?.generalConditions) return false;
      const requiredGeneralConditions = eventConfig.generalConditions.filter(c => c.required);
      const acceptedGeneralConditions = lectureData.generalConditions?.filter(c => c.accepted) || [];
      return requiredGeneralConditions.length > 0 && 
             requiredGeneralConditions.every(req => 
               acceptedGeneralConditions.some(acc => acc.id === req.id)
             );
      
    case 'conditions':
      // Check if all required lecture type specific conditions are accepted
      if (!lectureData.type || !eventConfig?.eventConditions) return false;
      const applicableConditions = eventConfig.eventConditions.filter(condition => 
        condition.applicableToTypes.includes(lectureData.type)
      );
      const requiredConditions = applicableConditions.filter(c => c.required);
      const acceptedConditions = lectureData.conditions?.filter(c => c.accepted) || [];
      return requiredConditions.length > 0 && 
             requiredConditions.every(req => 
               acceptedConditions.some(acc => acc.id === req.id)
             );
      
    case 'handout':
      // Only completed if at least one handout (DE or EN) is actually uploaded
      return lectureData.handout !== null && 
             lectureData.handout !== undefined && 
             (lectureData.handout.de || lectureData.handout.en);
      
    case 'presentation':
      // Only completed if presentation is actually uploaded
      return lectureData.presentation !== null && lectureData.presentation !== undefined;
      
    case 'communicate':
      // Keep manual - only completed through explicit user action
      return false;
      
    case 'lecture_type':
      return !!lectureData.type;
      
    case 'language': 
      return !!lectureData.language;
      
    case 'speakers':
      if (!lectureData.speakers || !Array.isArray(lectureData.speakers) || lectureData.speakers.length === 0) {
        return false;
      }

      // Get speaker requirements from event config
      const speakerReqs = getSpeakerRequirementsForType(eventConfig, lectureData.type);
      const minSpeakers = speakerReqs.min;

      // Check minimum speaker count and main speaker requirement
      const mainSpeakers = lectureData.speakers.filter(speaker => speaker.type === 'main_speaker');
      return lectureData.speakers.length >= minSpeakers &&
             mainSpeakers.length >= 1 &&
             mainSpeakers.every(speaker => {
               // Speaker must have either employeeId or userId
               if (speaker.speakerType === 'employee') {
                 return speaker.employeeId && speaker.employeeId.length > 0;
               } else if (speaker.speakerType === 'user') {
                 return speaker.userId && speaker.userId.length > 0;
               }
               // Backward compatibility
               return (speaker.employeeId && speaker.employeeId.length > 0) ||
                      (speaker.userId && speaker.userId.length > 0);
             });
      
    case 'title':
      // Check title validation like in schema (requires both de/en with proper length)
      if (!Array.isArray(lectureData.title) || lectureData.title.length < 2) return false;
      const titleRequiredLanguages = ['de', 'en'];
      const titleProvidedLanguages = lectureData.title.map(item => item.language).filter(Boolean);
      const titleMissingLanguages = titleRequiredLanguages.filter(lang => !titleProvidedLanguages.includes(lang));
      if (titleMissingLanguages.length > 0) return false;
      
      // Check if all required language entries have valid text (10-60 chars)
      const minTitleLength = eventConfig?.customValidations?.['title.text.minLength'] || 10;
      const maxTitleLength = eventConfig?.customValidations?.['title.text.maxLength'] || 60;
      return lectureData.title
        .filter(item => titleRequiredLanguages.includes(item.language))
        .every(item => item.text && item.text.trim().length >= minTitleLength && item.text.length <= maxTitleLength);
      
    case 'description':
      // Check content validation like in schema (requires both de/en with proper length)
      if (!Array.isArray(lectureData.content) || lectureData.content.length < 2) return false;
      const contentRequiredLanguages = ['de', 'en'];
      const contentProvidedLanguages = lectureData.content.map(item => item.language).filter(Boolean);
      const contentMissingLanguages = contentRequiredLanguages.filter(lang => !contentProvidedLanguages.includes(lang));
      if (contentMissingLanguages.length > 0) return false;
      
      // Check if all required language entries have valid text (500-5000 chars for lounges26)
      const minContentLength = eventConfig?.customValidations?.['content.text.minLength'] || 450;
      const maxContentLength = eventConfig?.customValidations?.['content.text.maxLength'] || 2000;
      return lectureData.content
        .filter(item => contentRequiredLanguages.includes(item.language))
        .every(item => item.text && item.text.trim().length >= minContentLength && item.text.length <= maxContentLength);
      
    case 'topics':
      if (!Array.isArray(lectureData.topics)) return false;
      const topicsLength = lectureData.topics.length;
      const minTopics = eventConfig?.customValidations?.['topics.min'] || 1;
      const maxTopics = eventConfig?.customValidations?.['topics.max'] || 3;
      return topicsLength >= minTopics && topicsLength <= maxTopics;
      
    case 'image':
      return !!lectureData.image;

    case 'booth_location':
      // Check if booth location (stand number) is selected
      return !!lectureData.companyBooth && lectureData.companyBooth.trim().length > 0;

    case 'subjects':
      // Check subjects validation like in SubjectsStep (3-5 subjects, at least 3 complete)
      if (!Array.isArray(lectureData.subjects)) return false;
      const subjectsLength = lectureData.subjects.length;
      if (subjectsLength < 3 || subjectsLength > 5) return false;
      
      // Count completely filled subjects
      const completeSubjects = lectureData.subjects.filter(subject => 
        subject.de && subject.de.trim().length > 0 && 
        subject.en && subject.en.trim().length > 0
      );
      
      // At least 3 must be complete
      if (completeSubjects.length < 3) return false;
      
      // Check no partially filled subjects
      return lectureData.subjects.every(subject => {
        const hasDeContent = subject.de && subject.de.trim().length > 0;
        const hasEnContent = subject.en && subject.en.trim().length > 0;
        // Either both filled or both empty
        return (hasDeContent && hasEnContent) || (!hasDeContent && !hasEnContent);
      });

    default:
      return false;
  }
};

// ========== EXPORTS ==========
export { 
  testEventSchema,
  lounges26Schema,
  createEventSpecificLectureSchema,
  languageTextSchema,
  speakerSchema,
  subjectSchema,
  conditionSchema,
  outlinePointSchema,
  getConditionsForLectureType,
  getRequiredConditionsForLectureType,
  isValidLectureType,
  getSpeakerRequirementsForType,
  getGeneralConditions,
  getRequiredGeneralConditions,
  getFieldValidationRules,
  getLectureTypeName,
  getTopicsLimits,
  isStepDataComplete
};

// ========== USAGE EXAMPLES ==========
/*
// Example 1: VIP Lecture for Lounges 26 (requires 2 speakers)
const vipLectureData = {
  companyLecturesId: "comp-123",
  type: "vip",
  title: [
    { language: "de", text: "Exklusiver VIP-Vortrag: Zukunft der Technologie" },
    { language: "en", text: "Exclusive VIP Lecture: Future of Technology" }
  ],
  speakers: [
    {
      id: "speaker-1",
      name: "Dr. Max Mustermann",
      title: "CTO",
      company: "Tech Innovations"
    },
    {
      id: "speaker-2", 
      name: "Prof. Jane Smith",
      title: "Research Director",
      company: "Future Labs"
    }
  ],
  content: [
    { 
      language: "de", 
      text: "In diesem exklusiven VIP-Vortrag erhalten Sie Einblicke in die neuesten Technologietrends und disruptive Innovationen, die unsere Zukunft prägen werden. ".repeat(5)
    }
  ],
  conditions: [
    { id: "data_protection", name: "Datenschutz", accepted: true },
    { id: "vip_special_terms", name: "VIP-Bedingungen", accepted: true }
  ],
  image: { fileName: "vip-lecture.jpg" },
  topics: ["Technologie", "Innovation"],
  eventLocation: "VIP Lounge",
  targetAudience: [
    { language: "de", text: "C-Level Executives und Entscheidungsträger" }
  ]
};

const vipValidation = validateLectureForEvent(vipLectureData, lounges26Schema);
console.log('VIP Lecture Validation:', vipValidation);

// Example 2: Technical Lecture for Lounges 26 (requires 1 speaker)
const technicalLectureData = {
  companyLecturesId: "comp-456",
  type: "technical",
  title: [
    { language: "de", text: "Technische Innovation in der Prozessoptimierung" }
  ],
  speakers: [
    {
      id: "speaker-3",
      name: "Ing. Klaus Weber",
      title: "Senior Engineer",
      company: "Process Experts"
    }
  ],
  content: [
    { 
      language: "de", 
      text: "Dieser technische Vortrag behandelt moderne Ansätze zur Prozessoptimierung in der Industrie 4.0. ".repeat(5)
    }
  ],
  conditions: [
    { id: "data_protection", name: "Datenschutz", accepted: true },
    { id: "technical_prerequisites", name: "Technische Voraussetzungen", accepted: true }
  ],
  image: { fileName: "technical-lecture.jpg" },
  topics: ["Prozessoptimierung", "Industrie 4.0"],
  eventLocation: "Technical Hall",
  targetAudience: [
    { language: "de", text: "Technische Fachkräfte und Ingenieure" }
  ]
};

const technicalValidation = validateLectureForEvent(technicalLectureData, lounges26Schema);
console.log('Technical Lecture Validation:', technicalValidation);

// Example 3: Helper Functions Usage
const vipConditions = getConditionsForLectureType(lounges26Schema, "vip");
console.log('VIP Conditions:', vipConditions);

const requiredConditions = getRequiredConditionsForLectureType(lounges26Schema, "vip");
console.log('Required VIP Conditions:', requiredConditions);

const isValidType = isValidLectureType(lounges26Schema, "educational");
console.log('Is educational type valid?', isValidType);

const speakerReqs = getSpeakerRequirementsForType(lounges26Schema, "vip");
console.log('VIP Speaker Requirements:', speakerReqs); // { min: 2 }

const techSpeakerReqs = getSpeakerRequirementsForType(lounges26Schema, "technical");
console.log('Technical Speaker Requirements:', techSpeakerReqs); // { min: 1 }
*/

// ========== LOUNGES 26 ACTIONS SCHEMA ==========
export const lounges26ActionsSchema = {
  eventId: "882c49b6-ba4b-4c66-8a35-987d04af08a5",
  eventType: "fair",
  entityType: "action",

  // Event dates configuration
  lectureRegistrationStartDate: "2025-11-01",
  lectureRegistrationEndDate: "2025-11-30",
  lectureEditEndDate: "2025-11-30",

  // Available action types
  availableLectureTypes: [
    "promotion_technical",
    "promotion_marketing"
  ],

  // General conditions for actions
  generalConditions: [
    {
      id: "submission_deadlines",
      title: [
        { language: "de", text: "Einreichungsfristen" },
        { language: "en", text: "Submission Deadlines" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich habe die Einreichungsfristen gelesen und akzeptiere diese." },
        { language: "en", text: "I have read and accept the submission deadlines." }
      ],
      content: [
        { language: "de", text: "Aktionen können ab dem 15. August 2026 eingereicht und müssen vollständig bis 30. November 2026 bestätigt werden. Nach dem 1. Dezember 2026 können keine Aktionen mehr eingereicht werden. Unvollständige Aktionen nach dem 30. November 2026 werden nicht ins Programm aufgenommen." },
        { language: "en", text: "Actions can be submitted from November 1, 2025 and must be fully confirmed by November 30, 2025. After December 1, 2025, no more actions can be submitted. Incomplete actions after November 30, 2025 will not be included in the program." }
      ],
      required: true
    },
    {
      id: "advertising_free_content",
      title: [
        { language: "de", text: "Werbefreiheit der Aktionen" },
        { language: "en", text: "Advertising-Free Actions" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich habe verstanden, dass die Aktion werbefrei sein muss und akzeptiere dies." },
        { language: "en", text: "I understand that the presentation must be advertising-free and accept this." }
      ],
      content: [
        { language: "de", text: "Aktionen zu Fachthemen sind werbefrei (keine Produktnamen, Datenblätter oder Firmeninformationen)." },
        { language: "en", text: "Technical actions must be advertising-free (no product names, data sheets or company information)." }
      ],
      required: true
    },
    {
      id: "speaker_expertise",
      title: [
        { language: "de", text: "Qualifikation der Referenten" },
        { language: "en", text: "Speaker Qualification" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich bestätige, dass die Referenten qualifiziert sind." },
        { language: "en", text: "I confirm that the speakers are qualified." }
      ],
      content: [
        { language: "de", text: "Referenten sind Experten ihres Themas und bieten qualitativ hochwertige und didaktisch wertvolle Aktionen." },
        { language: "en", text: "Speakers are experts in their field and provide high-quality and didactically valuable actions." }
      ],
      required: true
    },
    {
      id: "program_scheduling",
      title: [
        { language: "de", text: "Programmplanung" },
        { language: "en", text: "Program Planning" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich bin mit der Programmgestaltung / Zeiteinteilung durch den Veranstalter einverstanden." },
        { language: "en", text: "I agree with the program design / time scheduling by the organizer." }
      ],
      content: [
        { language: "de", text: "Die Programmzuordnung erfolgt durch den Veranstalter und wird dem Admin und Referent bis zum 31. Januar 2027 mitgeteilt." },
        { language: "en", text: "The program assignment is done by the organizer and will be communicated to the admin and speaker by January 31, 2026." }
      ],
      required: true
    }
  ],

  // Required fields
  requiredFields: [
    "type",
    "generalConditions",
    "title",
    "content",
    "subjects",
    "speakers",
    "image",
    "topics",
    "companyBooth"
  ],

  // Custom validation rules
  customValidations: {
    "content.text.minLength": 500,
    "content.text.maxLength": 2000,
    "title.text.minLength": 10,
    "title.text.maxLength": 60,
    "speakers.min": 1,
    "subjects.min": 3,
    "subjects.max": 5,
    "topics.min": 1,
    "topics.max": 3
  },

  // Field messages
  fieldMessages: {
    "type": "Aktionstyp",
    "companyBooth": "Standort"
  }
};